import { usePrepareCart } from '@cannect/hooks/usePrepareCart'
import { usePagaleveEligibility } from '@cannect/hooks/usePagaleveEligibility'
import { BRANDS_WITH_REQUIRED_FREIGHT, CLASS_WITHOUT_SHIPPING } from '@cannect/utils/constants'
import React, { useMemo } from 'react'
import { BiBarcodeReader, BiCreditCard } from 'react-icons/bi'
import { FaPix } from 'react-icons/fa6'
import { isProgramOrder, PROGRAM_INSTALLMENTS, PROGRAM_PIX_DISCOUNT_PERCENT } from 'pages/WeightLossProgram/constants'
import { WarningBanners } from '../resumeStep/WarningBanners'

interface PaymentMethodSelectionProps {
  selectedMethod: string
  onSelect: (method: string) => void
}

type PaymentOption = {
  id: string
  name: string
  description: string
  icon: React.ElementType
  discount?: string
}

export const PaymentMethodSelection: React.FC<PaymentMethodSelectionProps> = ({ selectedMethod, onSelect }) => {
  const { lastOrderCreated } = usePrepareCart()

  const hasOrderData = Boolean(lastOrderCreated?.person?.cpf && lastOrderCreated?.amount)
  const { isEligible: isPagaleveEligible, isChecking: isCheckingPagaleve } = usePagaleveEligibility({
    cpf: lastOrderCreated?.person?.cpf || '',
    amount: lastOrderCreated?.amount || 0,
    phone: lastOrderCreated?.person?.phone || '',
    email: lastOrderCreated?.person?.email || '',
    enabled: hasOrderData
  })

  const isProgramCart = isProgramOrder(lastOrderCreated)

  const uniquePayments: PaymentOption[] = useMemo(
    () =>
      [
        {
          id: 'credit',
          name: 'Crédito',
          description: isProgramCart ? `Em até ${PROGRAM_INSTALLMENTS}x` : 'Até 12x',
          icon: BiCreditCard
        },
        {
          id: 'boleto',
          name: 'Boleto',
          description: 'Vencimento em 3 dias úteis',
          icon: BiBarcodeReader
        },
        {
          id: 'pix',
          name: 'PIX',
          description: isProgramCart
            ? `${PROGRAM_PIX_DISCOUNT_PERCENT}% de desconto`
            : 'Pagamento instantâneo (à vista)',
          icon: FaPix
        },
        {
          id: 'pagaleve',
          name: 'PIX Parcelado',
          description: 'Parcele em até 12x',
          icon: FaPix
        }
      ].filter((payment) => {
        if (payment.id === 'pagaleve') {
          return !isProgramCart && isPagaleveEligible
        }
        return true
      }),
    [isPagaleveEligible, isProgramCart]
  )

  const combinedPayments = [
    {
      id: 'two_cards',
      name: 'Dois cartões',
      description: 'Dividida em dois cartões',
      icon: BiCreditCard,
      icon2: BiCreditCard
    },
    {
      id: 'card_pix',
      name: 'Cartão + Pix',
      description: 'Pagamento instantâneo',
      icon: BiCreditCard,
      icon2: FaPix
    },
    {
      id: 'card_boleto',
      name: 'Cartão + Boleto',
      description: 'Parte no cartão, parte no boleto',
      icon: BiCreditCard,
      icon2: BiBarcodeReader
    }
  ]

  const renderOption = (
    id: string,
    name: string,
    description: string,
    Icon: React.ElementType,
    Icon2?: React.ElementType,
    discount?: string
  ) => {
    const isSelected = selectedMethod === id

    return (
      <label
        key={id}
        htmlFor={`payment-${id}`}
        className="flex w-full max-w-full cursor-pointer items-center gap-2 overflow-hidden rounded-md border p-2 transition md:gap-3 md:p-1">
        <input
          type="radio"
          id={`payment-${id}`}
          name="payment-method"
          value={id}
          checked={isSelected}
          onChange={() => onSelect(id)}
          className="h-4 w-4 flex-shrink-0 border-4 accent-primary-700"
        />

        {/* Ícones */}
        <div className="flex flex-shrink-0 items-center gap-1 text-gray-700">
          <Icon className="h-4 w-4 text-neutral-800" />
          {Icon2 && (
            <>
              <span className="text-xs text-neutral-800">+</span>
              <Icon2 className="h-4 w-4 text-neutral-800" />
            </>
          )}
        </div>

        <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <span className="truncate text-[13px] text-gray-900">
            {name} {discount && <span className="italic text-primary-600">{discount}</span>}
          </span>
          <span className="truncate text-[11px] text-neutral-500">{description}</span>
        </div>
      </label>
    )
  }

  const isAllSupplements = lastOrderCreated?.items.every(
    (item: any) =>
      CLASS_WITHOUT_SHIPPING.includes(item?.product?.class_id || item?.class_id) &&
      !BRANDS_WITH_REQUIRED_FREIGHT.includes(item?.product?.brand_id || item?.brand_id)
  )

  // Se não tiver dados do pedido OU se não tiver opções, mostrar skeleton SIMPLES
  if (!lastOrderCreated?.amount || lastOrderCreated?.amount === 0 || uniquePayments.length === 0) {
    return (
      <div className="w-full rounded-xl bg-white p-2 md:p-8" style={{ maxWidth: '100%', minWidth: 0 }}>
        <h2 className="mb-4 px-2 text-base font-semibold text-gray-900 md:px-0">Escolha como pagar</h2>
        <div className="py-16 text-center">
          <div
            className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-gray-200"
            style={{ borderTopColor: '#748351' }}
          />
          <p className="mt-4 text-sm text-gray-600">Carregando opções de pagamento...</p>
        </div>
      </div>
    )
  }

  if (isCheckingPagaleve) {
    return (
      <div className="w-full rounded-xl bg-white p-2 md:p-8" style={{ maxWidth: '100%', minWidth: 0 }}>
        <h2 className="mb-4 px-2 text-base font-semibold text-gray-900 md:px-0">Escolha como pagar</h2>
        <div className="flex w-full flex-col gap-4 px-2 py-2 md:gap-6 md:px-2">
          {/* Skeleton - Pagamento único */}
          <div className="h-auto w-full max-w-full overflow-hidden rounded-lg bg-[#FAFAFA] p-3 md:p-6">
            <h3 className="mb-3 text-sm font-semibold text-neutral-800">Pagamento único</h3>
            <div className="flex flex-col space-y-2">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="h-12 w-full animate-pulse rounded-md bg-gray-200"
                  style={{ animationDelay: `${i * 100}ms` }}
                />
              ))}
            </div>
          </div>
          {/* Skeleton - Pagamento combinado */}
          <div className="h-auto w-full max-w-full overflow-hidden rounded-lg bg-[#FAFAFA] p-3 md:p-6">
            <h3 className="mb-3 text-sm font-semibold text-neutral-800">Pagamento combinado</h3>
            <div className="flex flex-col space-y-2">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="h-12 w-full animate-pulse rounded-md bg-gray-200"
                  style={{ animationDelay: `${i * 100}ms` }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full rounded-xl bg-white p-2 md:p-8" style={{ maxWidth: '100%', minWidth: 0 }}>
      <h2 className="mb-4 px-2 text-base font-semibold text-gray-900 md:px-0">Escolha como pagar</h2>

      <div className="flex w-full flex-col gap-4 px-2 py-2 md:gap-6 md:px-2">
        {/* Pagamento único */}
        <div className="h-auto w-full max-w-full overflow-hidden rounded-lg bg-[#FAFAFA] p-3 md:p-6">
          <h3 className="mb-3 text-sm font-semibold text-neutral-800">Pagamento único</h3>
          <div className="flex flex-col space-y-2">
            {uniquePayments.map((p) => renderOption(p.id, p.name, p.description, p.icon, undefined))}
          </div>
        </div>

        {!isProgramCart && (
          <div className="h-auto w-full max-w-full overflow-hidden rounded-lg bg-[#FAFAFA] p-3 md:p-6">
            <h3 className="mb-3 text-sm font-semibold text-neutral-800">Pagamento combinado</h3>
            <div className="flex flex-col space-y-2">
              {combinedPayments.map((p) => renderOption(p.id, p.name, p.description, p.icon, p.icon2))}
            </div>
          </div>
        )}
      </div>

      {!isAllSupplements && (
        <div className="mt-3 px-2 md:px-0">
          <WarningBanners
            containsProductWithLongerDeliveryTime={false}
            containProductsMatirz={false}
            containsProductWithHubDistributor={false}
          />
        </div>
      )}
    </div>
  )
}
